import React, { useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import GetAppIcon from '@material-ui/icons/GetApp';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { getProductTypeCapacityReport, downloadProductTypeCapacityReportExcel } from '../modules/prodplanningdata';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';

const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: '10px',
    },
    btn: {
        margin: '0.5em',
    },
}));

const dateRange = { start: moment().subtract(1, 'month'), end: moment().add(6, 'month') };

const generateWeekList = () => {
    const weeks = [];
    const running = moment(dateRange.start).day(4); // anchor to Thursday for stable ISO week
    const end = dateRange.end;
    while (running.isSameOrBefore(end, 'week')) {
        weeks.push({ id: running.format('YYYYww'), label: running.format('W'), year: running.format('GGGG') });
        running.add(1, 'week');
    }
    return weeks;
};

const weekList = generateWeekList();
const currentWeekId = moment().format('YYYYww');

const barColor = (pct) => {
    if (pct >= 1) return '#ef9a9a';
    if (pct >= 0.9) return '#ffcc80';
    if (pct >= 0.7) return '#fff176';
    return '#a5d6a7';
};

const CapacityCell = ({ weeklyData, field, capacityField, isCurrentWeek }) => {
    const allocated = weeklyData ? (weeklyData[capacityField] != null ? weeklyData[capacityField] : 0) : 0;
    const used = weeklyData ? (weeklyData[field] != null ? weeklyData[field] : 0) : 0;
    if (!weeklyData || allocated === 0) {
        return (
            <TableCell align="center" sx={{ color: '#bbb', fontWeight: isCurrentWeek ? 'bold' : 'normal' }}>
                —
            </TableCell>
        );
    }
    const pct = Math.min(used / allocated, 1);
    const fillPct = Math.round(pct * 100);
    const color = barColor(pct);
    return (
        <TableCell
            align="center"
            sx={{
                fontWeight: isCurrentWeek ? 'bold' : 'normal',
                whiteSpace: 'nowrap',
                background: `linear-gradient(to right, ${color} ${fillPct}%, transparent ${fillPct}%)`,
                outline: isCurrentWeek ? '2px solid #1A9C9F' : undefined,
                outlineOffset: '-2px',
            }}>
            {used}/{allocated}
        </TableCell>
    );
};

const ProductionCapacityReport2 = (props) => {
    const { t } = useTranslation();
    const active = props.active;
    const classes = useStyles();
    const [reportData, setReportData] = React.useState(null);

    useEffect(() => {
        async function loadReportData() {
            var reportDat = await getProductTypeCapacityReport();
            if (reportDat) setReportData(reportDat);
        }

        if (active && !reportData) {
            loadReportData();
        }
    }, [active, reportData]);

    const handleDownloadExcel = async () => {
        const fileName = `capacity_report_${moment().format('YYYY-MM-DD')}.xlsx`;
        await downloadProductTypeCapacityReportExcel(fileName);
    };

    if (!reportData) return null;
    if (!active) return null;

    return (
        <Grid container className={classes.root}>
            <Grid item xs={12} style={{ marginTop: '20px' }}>
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, minWidth: 160, fontWeight: 'bold' }}>
                                    {t('admin.capacityGroup')}
                                </TableCell>
                                {weekList.map((w) => (
                                    <TableCell
                                        key={w.id}
                                        align="center"
                                        sx={{
                                            minWidth: 64,
                                            fontWeight: w.id === currentWeekId ? 'bold' : 'normal',
                                            borderBottom: w.id === currentWeekId ? '2px solid #1A9C9F' : undefined,
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {w.label}
                                        <br />
                                        <span style={{ fontSize: '0.65em', color: '#888' }}>{w.year}</span>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reportData.map((groupReport) => {
                                const hasUrgentLane = groupReport.capacities.some((c) => c.urgentCapacity > 0);
                                const groupBorderSx = { borderTop: '2px solid #e0e0e0' };
                                return (
                                    <React.Fragment key={groupReport.capacityGroup.id}>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    bgcolor: 'background.paper',
                                                    zIndex: 1,
                                                    fontWeight: 500,
                                                    ...groupBorderSx,
                                                }}>
                                                {groupReport.capacityGroup.name}
                                                {hasUrgentLane && (
                                                    <span style={{ fontSize: '0.75em', color: '#555', marginLeft: 6 }}>
                                                        (N)
                                                    </span>
                                                )}
                                            </TableCell>
                                            {weekList.map((w) => {
                                                const weeklyData = groupReport.capacities.find((c) => c.week === w.id);
                                                return (
                                                    <CapacityCell
                                                        key={w.id}
                                                        weeklyData={weeklyData}
                                                        field="normalUsedCapacity"
                                                        capacityField="normalCapacity"
                                                        isCurrentWeek={w.id === currentWeekId}
                                                    />
                                                );
                                            })}
                                        </TableRow>
                                        {hasUrgentLane && (
                                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                                <TableCell
                                                    sx={{
                                                        position: 'sticky',
                                                        left: 0,
                                                        bgcolor: '#fafafa',
                                                        zIndex: 1,
                                                        color: '#b75383',
                                                        fontSize: '0.85em',
                                                        pl: 3,
                                                    }}>
                                                    {groupReport.capacityGroup.name}{' '}
                                                    <span style={{ fontWeight: 600 }}>({t('reports.urgent')})</span>
                                                </TableCell>
                                                {weekList.map((w) => {
                                                    const weeklyData = groupReport.capacities.find(
                                                        (c) => c.week === w.id
                                                    );
                                                    return (
                                                        <CapacityCell
                                                            key={w.id}
                                                            weeklyData={weeklyData}
                                                            field="urgentUsedCapacity"
                                                            capacityField="urgentCapacity"
                                                            isCurrentWeek={w.id === currentWeekId}
                                                        />
                                                    );
                                                })}
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            <Grid item xs={12} style={{ marginTop: '20px' }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<GetAppIcon />}
                    onClick={handleDownloadExcel}
                    className={classes.btn}>
                    {t('product.loadXlsx')}
                </Button>
            </Grid>
        </Grid>
    );
};

export default ProductionCapacityReport2;
